import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Modal, Button, OverlayTrigger, Popover, Table, Alert, Form as BsForm } from '../ui-kit';
import { IconExclamationTriangle, IconCircleCheck, IconTrash, IconPlus } from '../ui-kit/icons';
import { Formik, FieldArray } from 'formik';
import {
  operations as payrollPositionOperations,
  selectors as payrollPositionSelectors
} from '../../state/modules/payroll-positions';
import { overlaps, isActive } from '../../utils/schedules';
import Loader from '../general/Loader';
import { Form, DateTimeInput, Select, TextField, SubmitButton } from '../general/forms/bootstrap-formik';
import { crewTypesI18n } from '../general/CrewTypeLabel';
import { FormattedPeriod } from '../../utils/time';

const OverlappingPositionsPopover = ({ position, otherPositions }) => {
  // if we only have one position, we cannot have overlapping ones
  if (otherPositions.length <= 1) return false;

  const overlappingPositions = otherPositions.filter(
    otherPosition =>
      otherPosition.id !== position.id &&
      overlaps(position.start_time, position.end_time, otherPosition.start_time, otherPosition.end_time)
  );

  if (overlappingPositions.length === 0) return null;

  return (
    <OverlayTrigger
      trigger={['hover', 'focus']}
      placement="bottom"
      overlay={
        <Popover id="overlapping-positions-popover">
          <Popover.Body>
            <ul>
              {overlappingPositions.map(pos => (
                <li key={pos.id}>
                  {pos.name}
                  {': '}
                  <FormattedPeriod start={pos.start_time} end={pos.end_time} detailed={true} />
                </li>
              ))}
            </ul>
          </Popover.Body>
        </Popover>
      }
    >
      <IconExclamationTriangle className="text-warning ms-2" title={I18n.t('payroll.positions.warnings.overlapping')} />
    </OverlayTrigger>
  );
};

const renderIndividualPositionForm = ({ name, form, remove, push }) => {
  const values = (form && form.values[name]) || [];

  return (
    <Table condensed>
      <thead>
        <tr>
          <th style={{ minWidth: '52px' }}>#</th>
          <th>{I18n.t('activerecord.attributes.position.name')}</th>
          <th>{I18n.t('activerecord.attributes.position.role_number')}</th>
          <th>{I18n.t('activerecord.attributes.position.cost_center')}</th>
          <th>{I18n.t('activerecord.attributes.position.crew_type')}</th>
          <th style={{ minWidth: '260px' }}>{I18n.t('attributes.start_time')}</th>
          <th style={{ minWidth: '300px' }}>{I18n.t('attributes.end_time')}</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {values.map((value, index) => (
          <tr key={index}>
            <td>
              <BsForm.Group>
                <div className="form-control-plaintext">
                  <b>{index + 1}</b>
                  <OverlappingPositionsPopover position={value} otherPositions={values} />
                  {isActive(value.start_time, value.end_time) && (
                    <IconCircleCheck className="text-success ms-2" title={I18n.t('groups.active')} />
                  )}
                </div>
              </BsForm.Group>
            </td>
            <td>
              <TextField name={`${name}.${index}.name`} inputOnly />
            </td>
            <td>
              <TextField name={`${name}.${index}.role_number`} inputOnly />
            </td>
            <td>
              <TextField name={`${name}.${index}.cost_center`} inputOnly />
            </td>
            <td>
              <Select name={`${name}.${index}.crew_type`} options={crewTypesI18n} inputOnly />
            </td>
            <td>
              <DateTimeInput name={`${name}.${index}.start_time`} customLabel={I18n.t('from')} inputOnly />
            </td>
            <td>
              <DateTimeInput
                name={`${name}.${index}.end_time`}
                showClearButton={true}
                placeholder={I18n.t('open_ended')}
                inputOnly
              />
            </td>
            <td>
              <Button variant="danger" type="button" onClick={() => remove(index)}>
                <IconTrash />
              </Button>
            </td>
          </tr>
        ))}
        <tr>
          <td colSpan={8}>
            <Button
              onClick={() =>
                push({
                  id: globalThis.crypto.randomUUID(),
                  name: '',
                  role_number: '',
                  cost_center: '',
                  crew_type: 'unknown',
                  start_time: moment(),
                  end_time: ''
                })
              }
            >
              <IconPlus />
            </Button>
          </td>
        </tr>
      </tbody>
    </Table>
  );
};

const PositionForm = props => {
  return (
    <Modal dialogClassName="modal-xl modal-xxl" show={true} onHide={props.onHide}>
      <Form>
        <Modal.Header closeButton>
          <Modal.Title>{I18n.t('payroll.positions.manage')}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ overflowY: 'visible' }}>
          {props.showSavedSuccessfulMessage && <Alert variant="success">{I18n.t('changes_saved_successfully')}</Alert>}
          {props.initialLoad && <FieldArray name="positions" render={renderIndividualPositionForm} />}
          {!props.initialLoad && <Loader />}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={props.onHide}>
            {I18n.t('close')}
          </Button>
          <SubmitButton />
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const validateForm = values => {
  let errors = {};
  if (values.positions) {
    const positionErrors = values.positions.map(position => {
      const positionError = {};
      if (!position.role_number) {
        positionError.role_number = I18n.t('errors.messages.blank');
      }
      if (position.end_time && moment(position.start_time).isAfter(position.end_time)) {
        positionError.end_time = I18n.t('errors.messages.after', {
          restriction: I18n.t('attributes.start_time').toLowerCase()
        });
      }

      return Object.keys(positionError).length > 0 ? positionError : undefined;
    });

    if (positionErrors.some(Boolean)) {
      errors.positions = positionErrors;
    }
  }

  return errors;
};

const FormikPositionForm = props => {
  const [initialLoad, setInitialLoad] = useState(false);
  const [showSavedSuccessfulMessage, setShowSavedSuccessfulMessage] = useState(false);

  useEffect(() => {
    props.getPayrollPositions({ params: { membership_id: props.membershipId }, forceFetch: true }).then(() => {
      setInitialLoad(true);
    });
  }, []);

  const membershipPositions = useMemo(
    () => Object.values(props.payrollPositions).filter(p => p.membership_id === props.membershipId),
    [props.payrollPositions]
  );

  const storeMembershipPositions = useCallback(
    ({ positions }) => {
      return props.storeMembershipPositions(props.membershipId, positions);
    },
    [props.membershipId]
  );

  const toggleSavedSuccessfulMessage = () => {
    setShowSavedSuccessfulMessage(true);
    location.reload();
  };

  return (
    <Formik
      initialValues={{ positions: membershipPositions }}
      enableReinitialize={true}
      validate={validateForm}
      onSubmit={(values, { setSubmitting, _setStatus, _setErrors }) => {
        storeMembershipPositions(values)
          .then(() => {
            setSubmitting(false);
            toggleSavedSuccessfulMessage();
          })
          .catch(() => setSubmitting(false));
      }}
    >
      <PositionForm
        onHide={props.onHide}
        initialLoad={initialLoad}
        showSavedSuccessfulMessage={showSavedSuccessfulMessage}
      />
    </Formik>
  );
};

const mapStateToProps = state => ({
  payrollPositions: payrollPositionSelectors.byId(state)
});

const mapDispatchToProps = {
  getPayrollPositions: payrollPositionOperations.fetch,
  storeMembershipPositions: payrollPositionOperations.storeMembershipPositions
};

export default connect(mapStateToProps, mapDispatchToProps)(FormikPositionForm);
